import { isSdkLocalImageTimeout } from './image-delivery-status.js';

const UNCONFIRMED_CANCELLATION = 'Cancellation could not be confirmed. This image remains active or awaiting verification.';

// SDK 5.60.3's public cancel skips locally failed entities. Its local timeout
// notification uses this same public message, but does not wait for the receipt.
async function cancelLocallyTimedOutProject(socket, projectId, assertCurrent, timeoutMs) {
  let unsubscribe = () => {};
  let timeout;
  const confirmation = new Promise((resolve, reject) => {
    unsubscribe = socket.on('artistCancelConfirmation', data => {
      if (data.jobID !== projectId) return;
      try {
        assertCurrent();
        if (data.didCancel === true) resolve();
        else reject(new Error(data.error_message || UNCONFIRMED_CANCELLATION));
      } catch (error) { reject(error); }
    });
    timeout = setTimeout(() => reject(new Error(UNCONFIRMED_CANCELLATION)), timeoutMs);
  });
  try {
    assertCurrent();
    await Promise.all([
      socket.send('jobError', { jobID: projectId, error: 'artistCanceled',
        error_message: 'artistCanceled', isFromWorker: false }),
      confirmation
    ]);
    assertCurrent();
  } finally {
    unsubscribe();
    clearTimeout(timeout);
  }
}

/** Published SDK cancellation resolves only after artistCancelConfirmation
 * didCancel:true, except when a tracked project is already terminal. */
export async function cancelTrackedImageProject(projects, projectId,
  { socket, assertCurrent = () => {}, timeoutMs = 120000 } = {}) {
  const project = projects.trackedProjects.find(item => item.id === projectId);
  const localTimeout = () => project?.status === 'failed' && isSdkLocalImageTimeout(project.error);
  const terminal = () => project?.status === 'canceled'
    ? { confirmed: true, state: 'canceled', projectId }
    : project?.finished && !localTimeout() ? { state: 'finished', projectId, status: project.status } : null;
  const before = terminal();
  if (before) return before;
  try {
    assertCurrent();
    if (localTimeout()) await cancelLocallyTimedOutProject(socket, projectId, assertCurrent, timeoutMs);
    else await projects.cancel(projectId);
    assertCurrent();
    const after = terminal();
    return after || { confirmed: true, state: 'canceled', projectId };
  } catch (error) {
    return (error.code === 'SESSION_IDENTITY_CHANGED' ? null : terminal())
      || { confirmed: false, state: 'unconfirmed', projectId, error,
      ...(localTimeout() ? { unverified: true } : {}),
      message: error.message || 'Cancellation was not confirmed. This image remains active.' };
  }
}
